import { useState } from 'react';
import { Button } from '../../../ds/components';
import { CreateFinancingDialog } from '../../financings/create/CreateFinancingDialog';
import { useDealFinancings, useInvalidateDeals } from '../api';
import type { DealRecord } from '../types';
import styles from './financings.module.css';

/**
 * Finanzierungen 12: the warning above the tabs of an active property deal while a financing of it is lost and no
 * new one was created (`lostNotices` of `GET /deals/:id/financings`). „Hinweis ausblenden" is client-side only
 * (per notice, for the browser session); „Deal auf verloren setzen" opens the deal's lost dialog (reason
 * „Finanzierung abgelehnt" preselected there); „Neue Finanzierung anlegen" opens the create dialog.
 */

const HIDDEN_KEY = 'brimo.financingNotice.hidden';

function readHidden(): string[] {
  try {
    const raw = window.sessionStorage.getItem(HIDDEN_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

function writeHidden(ids: string[]): void {
  try {
    window.sessionStorage.setItem(HIDDEN_KEY, JSON.stringify(ids));
  } catch {
    // storage unavailable (private mode): the notice stays hidden for this page view only
  }
}

export function FinancingNotice({ deal, onLose }: { deal: DealRecord; onLose: () => void }) {
  const active = deal.status === 'active';
  const financings = useDealFinancings(deal.id, active);
  const invalidate = useInvalidateDeals();
  const [hidden, setHidden] = useState<string[]>(readHidden);
  const [creating, setCreating] = useState(false);

  const notices = active
    ? (financings.data?.lostNotices ?? []).filter((n) => !hidden.includes(n.financingId))
    : [];
  if (notices.length === 0 && !creating) return null;
  const canLose = deal.permissions.canLose && !deal.permissions.readOnly;
  const canCreate = financings.data?.canCreate ?? false;

  return (
    <>
      {notices.map((notice) => (
        <div key={notice.financingId} className={styles.notice} role="alert">
          <div className={styles.noticeText}>
            <div className={styles.noticeTitle}>{notice.title}</div>
            <div className={styles.noticeBody}>{notice.text}</div>
          </div>
          <span className={styles.noticeActions}>
            <Button
              variant="secondary"
              onClick={() => {
                const next = [...hidden, notice.financingId];
                setHidden(next);
                writeHidden(next);
              }}
            >
              Hinweis ausblenden
            </Button>
            {canLose ? (
              <Button variant="secondary" onClick={onLose}>
                Deal auf verloren setzen
              </Button>
            ) : null}
            {canCreate ? <Button onClick={() => setCreating(true)}>Neue Finanzierung anlegen</Button> : null}
          </span>
        </div>
      ))}
      {creating ? (
        <CreateFinancingDialog
          dealId={deal.id}
          onClose={(created) => {
            setCreating(false);
            if (created) void invalidate(deal.id);
          }}
        />
      ) : null}
    </>
  );
}
